import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';

interface FilterChipsProps<T extends string> {
  filters: readonly T[];
  value: T;
  onChange: (f: T) => void;
  labelPrefix: string;
}

export function FilterChips<T extends string>({
  filters,
  value,
  onChange,
  labelPrefix,
}: FilterChipsProps<T>) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createStyles(theme), [theme]);

  return (
    <View style={styles.row}>
      {filters.map((f) => {
        const active = f === value;
        const key = `${labelPrefix}${f.charAt(0).toUpperCase()}${f.slice(1)}`;
        return (
          <TouchableOpacity
            key={f}
            style={[styles.chip, active && styles.chipActive]}
            onPress={() => onChange(f)}
            accessibilityRole="button"
            accessibilityState={{ selected: active }}
          >
            <Text style={[styles.chipText, active && styles.chipTextActive]}>{t(key)}</Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

function createStyles(theme: any) {
  return StyleSheet.create({
    row: { flexDirection: 'row', gap: theme.spacing.xs, marginBottom: theme.spacing.sm },
    chip: {
      paddingHorizontal: theme.spacing.sm,
      paddingVertical: theme.spacing.xxs,
      borderRadius: theme.radii.sm,
      borderWidth: 1,
      borderColor: theme.colors.border,
    },
    chipActive: { backgroundColor: theme.colors.primary, borderColor: theme.colors.primary },
    chipText: { color: theme.colors.textSecondary, fontSize: theme.typography.fontSizeXs },
    chipTextActive: {
      color: theme.colors.textInverse,
      fontWeight: theme.typography.fontWeightBold,
    },
  });
}
